import { Link } from "react-router-dom";
import { ApiError, get, type MyStudent, type MyStudentDetail } from "../api.js";
import { booking, type LessonCreditSummary } from "../booking/api.js";
import { InboxSummary } from "../inbox/summary.js";
import { formatDate, formatTime } from "../format.js";
import { getFamilyPracticeTodos, type PracticeTodo } from "../practiceTodos.js";
import { useAsync } from "../useAsync.js";

type HouseholdBooking = MyStudentDetail["upcomingBookings"][number] & {
  domain?: "private" | "school";
  contextLabel?: string;
  canManageBooking?: boolean;
};

interface LearnerReads {
  learner: MyStudent;
  detail: MyStudentDetail | null;
  practiceTasks: PracticeTodo[] | null;
  credits: LessonCreditSummary | null;
  detailFailed: boolean;
  practiceFailed: boolean;
  creditsFailed: boolean;
  accessChanged: boolean;
}

interface HouseholdLoad {
  key: string;
  learners: LearnerReads[];
}

interface CalendarEntry {
  key: string;
  learner: MyStudent;
  booking: HouseholdBooking;
  startsAt: number;
  endsAt: number;
}

async function loadLearner(
  learner: MyStudent,
  bookingConfigured: boolean,
): Promise<LearnerReads> {
  const canReadCredits = bookingConfigured && learner.privateBooking;
  const reads: [
    Promise<MyStudentDetail>,
    Promise<PracticeTodo[]>,
    Promise<LessonCreditSummary | null>,
  ] = [
    get<MyStudentDetail>("/me/students/" + encodeURIComponent(learner.id), {
      lessonNotesPage: "1",
      lessonNotesPageSize: "1",
    }),
    getFamilyPracticeTodos(learner.id),
    canReadCredits ? booking.credits(learner.id) : Promise.resolve(null),
  ];
  const [detailResult, practiceResult, creditsResult] = await Promise.allSettled(reads);

  const accessChanged = [detailResult, practiceResult, creditsResult].some(
    (result) =>
      result.status === "rejected" &&
      result.reason instanceof ApiError &&
      [403, 404].includes(result.reason.status),
  );
  const detail =
    !accessChanged &&
    detailResult.status === "fulfilled" &&
    detailResult.value.student.id === learner.id
      ? detailResult.value
      : null;
  const practiceTasks =
    !accessChanged && practiceResult.status === "fulfilled"
      ? practiceResult.value
      : null;
  const rawCredits =
    !accessChanged && creditsResult.status === "fulfilled"
      ? creditsResult.value
      : null;
  const credits =
    rawCredits && rawCredits.portal_student_id === learner.id ? rawCredits : null;

  return {
    learner,
    detail,
    practiceTasks,
    credits,
    detailFailed: detail === null,
    practiceFailed: practiceTasks === null,
    creditsFailed:
      canReadCredits &&
      (accessChanged || credits === null || creditsResult.status === "rejected"),
    accessChanged,
  };
}

function householdEvents(learners: LearnerReads[]): CalendarEntry[] {
  return learners.flatMap(({ learner, detail }) =>
    (detail?.upcomingBookings ?? []).flatMap((item) => {
      const event = item as HouseholdBooking;
      const startsAt = Date.parse(event.startsAt);
      const endsAt = Date.parse(event.endsAt);
      if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt) || endsAt <= startsAt) {
        return [];
      }
      return [{
        key: learner.id + ":" + event.appointmentId,
        learner,
        booking: event,
        startsAt,
        endsAt,
      }];
    }),
  ).sort((left, right) => left.startsAt - right.startsAt);
}

function overlappingEvents(events: CalendarEntry[]): Set<string> {
  const overlapping = new Set<string>();
  for (let leftIndex = 0; leftIndex < events.length; leftIndex += 1) {
    const left = events[leftIndex]!;
    for (let rightIndex = leftIndex + 1; rightIndex < events.length; rightIndex += 1) {
      const right = events[rightIndex]!;
      if (
        right.startsAt >= left.endsAt ||
        left.learner.id === right.learner.id ||
        left.startsAt >= right.endsAt
      ) {
        continue;
      }
      overlapping.add(left.key);
      overlapping.add(right.key);
    }
  }
  return overlapping;
}

function privateCreditBlocks(reads: LearnerReads): LessonCreditSummary["blocks"] {
  return (reads.credits?.blocks ?? []).filter((block) => block.family_shared !== true);
}

function householdAvailableCredits(block: LessonCreditSummary["blocks"][number]): number | null {
  if (block.status === "active") {
    return Number.isFinite(block.available_credits) && block.available_credits >= 0
      ? block.available_credits
      : null;
  }
  if (["requires_review", "expired", "depleted", "cancelled", "refunded"].includes(block.status)) {
    return 0;
  }
  return null;
}

function householdCreditAvailabilityLabel(
  block: LessonCreditSummary["blocks"][number],
): string {
  const available = householdAvailableCredits(block);
  if (available === null) return "Availability not confirmed by the booking authority";
  return `${available} available`;
}

function householdCreditCountLabel(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "Not reported by the booking authority";
  }
  return String(value) + " " + (Math.abs(value) === 1 ? "credit" : "credits");
}

function householdCreditStatusLabel(status: string): string {
  switch (status) {
    case "active": return "Active";
    case "household_snapshot_conflict": return "Different authority snapshots";
    case "requires_review": return "Under review";
    case "expired": return "Expired";
    case "depleted": return "Depleted";
    case "cancelled": return "Cancelled";
    case "refunded": return "Refunded";
    default: return "Not confirmed";
  }
}

function HouseholdCreditBreakdown({
  block,
}: {
  block: LessonCreditSummary["blocks"][number];
}) {
  return (
    <details className="ui-disclosure household-credit-breakdown">
      <summary>Balance breakdown</summary>
      <div className="ui-disclosure-body">
        <dl className="small">
          <div><dt>Available to book</dt><dd>{householdCreditAvailabilityLabel(block)}</dd></div>
          <div><dt>Ledger balance</dt><dd>{householdCreditCountLabel(block.ledger_balance)}</dd></div>
          <div><dt>Temporary booking holds</dt><dd>{householdCreditCountLabel(block.booking_held_credits)}</dd></div>
          <div><dt>Consumed from this block</dt><dd>{householdCreditCountLabel(block.consumed_credits)}</dd></div>
          <div><dt>Completed lesson use</dt><dd>{householdCreditCountLabel(block.completed_used_credits)}</dd></div>
          <div><dt>Upcoming allocated use</dt><dd>{householdCreditCountLabel(block.upcoming_allocated_credits)}</dd></div>
          <div><dt>Ledger state</dt><dd>{householdCreditStatusLabel(block.status)}</dd></div>
        </dl>
        <p className="small muted">
          Only the available-to-book figure is spendable. Holds stay separate.
          Upcoming and completed use appear separately only when the booking
          authority supplies them; missing values are not used to estimate
          when credits will run out.
        </p>
        {block.status === "household_snapshot_conflict" && (
          <p className="small muted" role="status">
            The booking authority returned different shared-credit snapshots at
            the same evaluation time. Check the booking page before relying on
            this balance.
          </p>
        )}
        {block.family_shared === true && (
          <p className="small muted">
            This family-shared block may appear in more than one learner’s source
            record. It is listed once here; do not add balances across children.
          </p>
        )}
      </div>
    </details>
  );
}
function sameSharedCreditSnapshot(
  left: LessonCreditSummary["blocks"][number],
  right: LessonCreditSummary["blocks"][number],
): boolean {
  return left.status === right.status
    && left.available_credits === right.available_credits
    && (left.ledger_balance ?? null) === (right.ledger_balance ?? null)
    && (left.booking_held_credits ?? null) === (right.booking_held_credits ?? null)
    && (left.consumed_credits ?? null) === (right.consumed_credits ?? null)
    && (left.completed_used_credits ?? null) === (right.completed_used_credits ?? null)
    && (left.upcoming_allocated_credits ?? null) === (right.upcoming_allocated_credits ?? null)
    && left.expires_at === right.expires_at;
}

function householdSharedCreditBlocks(
  learners: LearnerReads[],
): LessonCreditSummary["blocks"] {
  const grouped = new Map<string, {
    block: LessonCreditSummary["blocks"][number];
    evaluatedAt: number;
  }[]>();
  for (const learner of learners) {
    if (!learner.credits) continue;
    const evaluatedAt = Date.parse(learner.credits.evaluated_at);
    for (const block of learner.credits.blocks) {
      if (block.family_shared !== true) continue;
      const observations = grouped.get(block.lesson_block_id) ?? [];
      observations.push({ block, evaluatedAt });
      grouped.set(block.lesson_block_id, observations);
    }
  }

  const blocks: LessonCreditSummary["blocks"] = [];
  for (const observations of grouped.values()) {
    const normalizedTime = (value: number) => Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
    const latestTime = Math.max(...observations.map((item) => normalizedTime(item.evaluatedAt)));
    const latest = observations.filter((item) => normalizedTime(item.evaluatedAt) === latestTime);
    const first = latest[0]?.block;
    if (!first) continue;
    if (latest.some((item) => !sameSharedCreditSnapshot(first, item.block))) {
      blocks.push({
        ...first,
        status: "household_snapshot_conflict",
        available_credits: Number.NaN,
        ledger_balance: undefined,
        booking_held_credits: undefined,
        consumed_credits: undefined,
        completed_used_credits: undefined,
        upcoming_allocated_credits: undefined,
      });
    } else {
      blocks.push(first);
    }
  }
  return blocks.sort((left, right) =>
    left.service_name.localeCompare(right.service_name) ||
    left.lesson_block_id.localeCompare(right.lesson_block_id),
  );
}
function FamilyCreditBalance({
  learners,
  enabled,
}: {
  learners: LearnerReads[];
  enabled: boolean;
}) {
  if (!enabled) return null;

  const sharedBlocks = householdSharedCreditBlocks(learners);
  const privateCreditReadUnavailable = learners.some(
    (item) => item.learner.privateBooking && item.creditsFailed,
  );
  const anyCreditRead = learners.some((item) => item.credits !== null);

  return (
    <section aria-labelledby="household-shared-credits-heading">
      <h2 id="household-shared-credits-heading">Family shared credits</h2>
      {sharedBlocks.length > 0 ? (
        <>
          <ul className="panel">
            {sharedBlocks.map((block) => (
              <li key={block.lesson_block_id}>
                <p>
                  {block.service_name}: {householdCreditAvailabilityLabel(block)}
                  {block.tutor_name ? " with " + block.tutor_name : ""}
                </p>
                <HouseholdCreditBreakdown block={block} />
              </li>
            ))}
          </ul>
          {privateCreditReadUnavailable && (
            <p className="small muted" role="status">
              Some private credit reads could not be confirmed, so additional shared
              credits may be missing. Check the booking page before booking.
            </p>
          )}
        </>
      ) : privateCreditReadUnavailable ? (
        <p className="small muted" role="status">
          Family shared credits could not be confirmed. Check the booking page before booking.
        </p>
      ) : anyCreditRead ? (
        <p className="small muted">No shared family credit blocks are currently available.</p>
      ) : (
        <p className="small muted" role="status">
          The family credit balance could not be confirmed. Check it before booking.
        </p>
      )}
    </section>
  );
}

/** Read-only overview across learners returned by the authorised /me/students list. */
export function HouseholdOverview({
  learners,
  bookingConfigured,
  sharedCreditsEnabled,
}: {
  learners: MyStudent[];
  bookingConfigured: boolean;
  sharedCreditsEnabled: boolean;
}) {
  const learnerKey = learners.map((learner) => learner.id).join("|");
  const result = useAsync<HouseholdLoad>(
    async () => ({
      key: learnerKey,
      learners: await Promise.all(
        learners.map((learner) => loadLearner(learner, bookingConfigured)),
      ),
    }),
    [learnerKey, bookingConfigured],
  );
  const loaded =
    !result.loading && !result.error && result.data?.key === learnerKey
      ? result.data.learners
      : null;
  const events = loaded ? householdEvents(loaded) : [];
  const overlaps = overlappingEvents(events);

  return (
    <div className="family-household-overview">
      <p><Link to="/home">Back to your selected learner’s Home</Link></p>
      <h1>Household overview</h1>
      <p>
        Upcoming lessons, current practice tasks and private credit balances for
        learners linked to this account. School lessons are read-only; booking
        actions appear only for learners with private booking access.
      </p>

      <InboxSummary />

      {result.loading && !loaded && (
        <p role="status">Loading household information…</p>
      )}
      {result.error && (
        <div className="panel">
          <p className="error" role="alert">
            The household overview could not be loaded.
          </p>
          <button type="button" onClick={result.reload}>Try again</button>
        </div>
      )}

      {loaded && (
        <>
          <FamilyCreditBalance
            learners={loaded}
            enabled={bookingConfigured && sharedCreditsEnabled}
          />

          <section aria-labelledby="household-calendar-heading">
            <h2 id="household-calendar-heading">Combined calendar</h2>
            {loaded.some((item) => item.detailFailed) && (
              <p className="small muted" role="status">
                Some lesson details could not be loaded. They are not treated as an empty schedule.
              </p>
            )}
            {overlaps.size > 0 && (
              <p className="panel warn" role="status">
                Some learners have lessons at overlapping times. This is a family
                scheduling warning; it does not mean a tutor or provider has
                double-booked a lesson.
              </p>
            )}
            {events.length === 0 ? (
              <p className="empty">
                {loaded.some((item) => item.detailFailed)
                  ? "No lesson data is available to show yet."
                  : "No upcoming lessons were returned."}
              </p>
            ) : (
              <ul className="panel family-household-calendar">
                {events.map((entry) => {
                  const event = entry.booking;
                  const isSchool =
                    event.domain === "school" ||
                    event.canManageBooking === false ||
                    (entry.learner.privateBooking === false && event.domain !== "private");
                  return (
                    <li key={entry.key}>
                      <time dateTime={event.startsAt}>
                        {formatDate(event.startsAt)}, {formatTime(event.startsAt)}
                        {"–"}{formatTime(event.endsAt)}
                      </time>
                      {" · "}
                      <strong>{entry.learner.isSelf ? "You" : entry.learner.fullName}</strong>
                      {" · "}
                      {event.serviceName ?? event.contextLabel ?? (isSchool ? "School lesson" : "Private lesson")}
                      {" · "}
                      <span className="tag">{isSchool ? "School" : "Private"}</span>
                      {event.awaitingConfirmation && (
                        <span className="tag warn">Awaiting confirmation</span>
                      )}
                      {overlaps.has(entry.key) && (
                        <span className="tag warn">Overlaps another learner’s lesson</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section aria-labelledby="household-learners-heading">
            <h2 id="household-learners-heading">Learners</h2>
            <div className="family-household-learners">
              {loaded.map((item, index) => {
                const activeTasks = (item.practiceTasks ?? []).filter(
                  (task) => !task.completed && !task.withdrawnAt,
                );
                const ownBlocks = privateCreditBlocks(item);
                const privateCredits = ownBlocks.reduce(
                  (total, block) => total + block.available_credits,
                  0,
                );
                const learnerName = item.learner.isSelf ? "You" : item.learner.fullName;
                return (
                  <article className="panel" key={item.learner.id}>
                    <h3 id={"household-learner-" + index}>{learnerName}</h3>
                    <p className="small muted">
                      {[item.learner.instrument, item.learner.tutorName]
                        .filter(Boolean)
                        .join(" · ") || "Learner"}
                    </p>
                    {item.accessChanged && (
                      <p className="small" role="status">
                        Access to this learner changed. Their details were removed from this overview.
                      </p>
                    )}

                    <section aria-labelledby={"household-practice-" + index}>
                      <h4 id={"household-practice-" + index}>Practice plan</h4>
                      {item.practiceFailed ? (
                        <p className="small" role="status">
                          Practice tasks could not be loaded.
                        </p>
                      ) : activeTasks.length === 0 ? (
                        <p className="small muted">No current practice tasks.</p>
                      ) : (
                        <ul>
                          {activeTasks.slice(0, 3).map((task) => (
                            <li key={task.id}>
                              {task.title}
                              {task.suggestedMinutes
                                ? " · " + task.suggestedMinutes + " minute guide"
                                : ""}
                            </li>
                          ))}
                        </ul>
                      )}
                      <Link to={"/practice?student=" + encodeURIComponent(item.learner.id)}>
                        Open {learnerName}’s practice
                      </Link>
                    </section>

                    <section aria-labelledby={"household-private-credits-" + index}>
                      <h4 id={"household-private-credits-" + index}>Private credits</h4>
                      {!bookingConfigured ? (
                        <p className="small muted">Private booking is not available right now.</p>
                      ) : !item.learner.privateBooking ? (
                        <p className="small muted">
                          This learner has School lessons only; private booking is not enabled.
                        </p>
                      ) : item.creditsFailed ? (
                        <p className="small" role="status">
                          The private credit balance could not be loaded.
                        </p>
                      ) : ownBlocks.length === 0 ? (
                        <p className="small muted">0 private lesson credits available.</p>
                      ) : (
                        <ul>
                          {ownBlocks.map((block) => (
                            <li key={block.lesson_block_id}>
                              <p>
                                {block.service_name}: {householdCreditAvailabilityLabel(block)}
                                {block.tutor_name ? " with " + block.tutor_name : ""}
                              </p>
                              <HouseholdCreditBreakdown block={block} />
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>

                    <div className="student-home-section-link">
                      <Link to={"/home?student=" + encodeURIComponent(item.learner.id)}>
                        Open {learnerName}’s Home
                      </Link>
                      {bookingConfigured && item.learner.privateBooking && (
                        <>
                          {" · "}
                          <Link to={"/book?student=" + encodeURIComponent(item.learner.id)}>
                            Book for {learnerName}
                          </Link>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
